import { Field } from '@atlaskit/form';
import { Text } from '@atlaskit/primitives';
import JiraServerPicker from '@databases/field-picker/JiraServerPicker';
import { messages as intlMessages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type JiraServer } from '@databases/types/confluence';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

type JiraLinkFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.JIRA_ISSUE>;
};

const JiraLinkFieldSettings: FunctionComponent<JiraLinkFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const selectJiraInstanceLabel = formatMessage(
    intlMessages['field-settings.type-jira-issue-select-jira-instance.non-final']
  );

  const handleChange = useCallback(
    (val: JiraServer | undefined) => {
      if (!val) {
        return;
      }

      const { id: jiraServerId, cloudId } = val;
      if (cloudId) {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_CLOUD_ID,
            fieldId: field.id,
            cloudId
          })
        );
      }

      if (jiraServerId) {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_JIRA_SERVER_ID,
            fieldId: field.id,
            jiraServerId
          })
        );
      }
    },
    [contentId, dispatch, field.id]
  );

  return (
    <Field
      label={
        <Text size="small" weight="bold" color="color.text.subtle">
          {selectJiraInstanceLabel}
        </Text>
      }
      id="jira-link-source-instance-picker-input"
      name="jira-link-source-instance-field"
      isRequired
    >
      {({ fieldProps }) => (
        <JiraServerPicker
          value={field.settings.cloudId ?? field.settings.jiraServerId}
          onChange={handleChange}
          isRequired
          isCompact
          inputId={fieldProps.id}
        />
      )}
    </Field>
  );
};

export default JiraLinkFieldSettings;
